var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva } from 'class-variance-authority';
import { classNames as cn } from './utils';
import { useDesktopLayout } from './useDesktopLayout';
const tabsVariants = cva('', {
    variants: {
        layout: {
            'default': 'bg-base-100 flex flex-col gap-2 rounded-xl',
            'responsive-sidebar': 'grid h-full w-full min-w-0 grid-cols-1 gap-6 overflow-hidden bg-transparent lg:grid-cols-[200px_1fr]',
        },
    },
    defaultVariants: {
        layout: 'default',
    },
});
const tabsListVariants = cva('bg-muted text-muted-foreground inline-flex w-fit items-center justify-center rounded-xl p-[3px] gap-1', {
    variants: {
        size: {
            sm: 'h-7',
            md: 'h-8 gap-0 rounded-2lg p-0.5',
            default: 'h-9',
            lg: 'h-11',
            adaptive: 'h-full',
            responsive: 'h-9',
        },
        layout: {
            'default': '',
            'responsive-sidebar': 'no-scrollbar h-min w-full min-w-0 flex-nowrap justify-start overflow-x-auto overflow-y-hidden bg-transparent p-0 lg:sticky lg:top-0 lg:flex-wrap lg:flex-col lg:items-start lg:overflow-x-visible',
        },
    },
    defaultVariants: {
        size: 'default',
        layout: 'default',
    },
});
const tabsTriggerVariants = cva('dark:data-[state=active]:text-base-900 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-base-300 text-base-500 dark:text-muted-foreground data-[state=active]:text-base-900 hover:bg-base-50 dark:hover:bg-base-300/50 inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-lg border border-transparent font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-card data-[state=active]:shadow-tabs [&_svg]:pointer-events-none [&_svg]:shrink-0', {
    variants: {
        size: {
            sm: 'px-1.5 py-0.5 text-xs [&_svg:not([class*="size-"])]:size-3',
            md: 'h-7 px-3.5 py-1.5 text-[13px] leading-4 [&_svg:not([class*="size-"])]:size-4',
            default: 'px-2 py-1 text-sm [&_svg:not([class*="size-"])]:size-4',
            lg: 'px-3 py-1.5 text-base [&_svg:not([class*="size-"])]:size-5',
            adaptive: 'px-2 py-1 text-sm [&_svg:not([class*="size-"])]:size-4',
            responsive: 'gap-1.5 px-2 py-1 text-sm [&_svg:not([class*="size-"])]:size-4',
        },
        layout: {
            'default': '',
            'responsive-sidebar': 'h-auto flex-none justify-start gap-2.5 p-2 data-[state=active]:bg-base-100 data-[state=active]:shadow-none lg:w-full dark:data-[state=active]:bg-base-200',
        },
    },
    defaultVariants: {
        size: 'default',
        layout: 'default',
    },
});
export function Tabs(_a) {
    var { className, layout } = _a, props = __rest(_a, ["className", "layout"]);
    const isDesktopLayout = useDesktopLayout();
    return (_jsx(TabsPrimitive.Root, Object.assign({ "data-slot": "tabs", className: cn(layout === 'responsive-sidebar' && isDesktopLayout
            ? 'grid h-full w-full grid-cols-[200px_1fr] gap-6 overflow-hidden rounded-xl bg-transparent'
            : tabsVariants({ layout }), className) }, props)));
}
export function TabsList(_a) {
    var { className, size, layout } = _a, props = __rest(_a, ["className", "size", "layout"]);
    const isDesktopLayout = useDesktopLayout();
    return (_jsx(TabsPrimitive.List, Object.assign({ "data-slot": "tabs-list", className: cn(tabsListVariants({
            size,
            layout: layout === 'responsive-sidebar' && isDesktopLayout
                ? 'default'
                : layout,
        }), size === 'responsive' && !isDesktopLayout && 'h-7', layout === 'responsive-sidebar' &&
            isDesktopLayout &&
            'sticky top-0 h-min w-full flex-col flex-wrap items-start justify-start bg-transparent p-0', className) }, props)));
}
export function TabsTrigger(_a) {
    var { className, size, layout } = _a, props = __rest(_a, ["className", "size", "layout"]);
    const isDesktopLayout = useDesktopLayout();
    return (_jsx(TabsPrimitive.Trigger, Object.assign({ "data-slot": "tabs-trigger", className: cn(tabsTriggerVariants({
            size,
            layout: layout === 'responsive-sidebar' && isDesktopLayout
                ? 'default'
                : layout,
        }), size === 'responsive' &&
            !isDesktopLayout &&
            'gap-1 px-1.5 py-0.5 text-xs [&_svg:not([class*="size-"])]:size-3', layout === 'responsive-sidebar' &&
            isDesktopLayout &&
            'data-[state=active]:bg-base-100 dark:data-[state=active]:bg-base-200 h-auto w-full flex-none justify-start gap-2.5 p-2 data-[state=active]:shadow-none', className) }, props)));
}
export function TabsContent(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(TabsPrimitive.Content, Object.assign({ "data-slot": "tabs-content", className: cn('flex-1 outline-none', className) }, props)));
}
export function TabsIconLabel(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return _jsx("span", Object.assign({ className: cn('hidden lg:inline', className) }, props));
}
